import { useState, useEffect, useCallback, useMemo } from 'react';
import { useInternetStatus } from '../components/shared/InternetStatus';
import {
  fetchStripePublicConfig,
  getStripePromise,
  isNetworkFailure,
  clearStripeConfigCache,
} from '../services/stripeConfigService';

/**
 * Loads Stripe.js using the publishable key from the API (vendor-booking-api .env only).
 */
const useStripeCheckout = () => {
  const { isOnline, isChecking, checkConnection } = useInternetStatus();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(
    async (force = false) => {
      if (!isOnline) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const data = await fetchStripePublicConfig({ force });
        setConfig(data);
      } catch (err) {
        const message =
          err.response?.data?.message ||
          (isNetworkFailure(err)
            ? 'Could not reach the server. Check your connection and try again.'
            : err.message || 'Failed to load Stripe configuration');
        setError(message);
        setConfig(null);
      } finally {
        setLoading(false);
      }
    },
    [isOnline]
  );

  useEffect(() => {
    if (isOnline) {
      loadConfig();
    } else {
      setLoading(false);
    }
  }, [isOnline, loadConfig]);

  const retry = useCallback(async () => {
    await checkConnection();
    clearStripeConfigCache();
    await loadConfig(true);
  }, [checkConnection, loadConfig]);

  const stripePromise = useMemo(() => {
    if (!isOnline || !config?.publishable_key) return null;
    return getStripePromise(config.publishable_key);
  }, [isOnline, config?.publishable_key]);

  return {
    isOnline,
    isChecking,
    config,
    mode: config?.mode ?? null,
    stripePromise,
    stripeReady: Boolean(stripePromise),
    loading: isOnline && loading,
    error,
    retry,
  };
};

export default useStripeCheckout;
